import React, { useState, useEffect } from 'react';
import DatePicker, { registerLocale } from "react-datepicker";
import MaskedInput from 'react-maskedinput';
import Select from "react-select";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

//Import Components
import api from '../../../services/api';
import SummarySlider from './SummarySlider';
import ReactApexChart from 'react-apexcharts';
import HospitalProcedure from './HospitalProcedure';
import ProceduresSummary, { buildAttendanceData } from './ProceduresSummary';
import HospitalGoal from './HospitalGoal';
import ExportButton from './ExportButton';
import { exportToExcel } from './exportExcel';

import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";
import HealthPlanProcedure from './HealthPlanProcedure';
import { ParticularId, SusId, UnimedId } from '../../../enum/fixedEntities';
import GeneralSummary from './GeneralSummary';
import { get } from 'lodash';
registerLocale("ptBR", ptBR);

const PIECOLORS = ["#ff5166", "#2BC844", "#9568FF", "#2A353A"];

const Dashboard = () => {
    const [doctor, setDoctor] = useState(null);
    const [doctorsList, setDoctorsList] = useState([]);

    const [procedure, setProcedure] = useState(null);
    const [proceduresList, setProceduresList] = useState([]);

    const [pacientsCount, setPacientsCount] = useState(0);
    const [proceduresCount, setProceduresCount] = useState(0);
    const [proceduresSummary, setProceduresSummary] = useState([]);

    const [proceduresTotals, setProceduresTotals] = useState([]);
    const [proceduresColors, setProceduresColors] = useState([]);

    const [hospitalsSummary, setHospitalsSummary] = useState([]);
    const [hospitalsList, setHospitalsList] = useState([]);

    const [healthPlansSummary, setHealthPlansSummary] = useState([]);

    const [generalSummary, setGeneralSummary] = useState({
        total: 0,
        other: {
            complex: 0,
            normal: 0,
            apto: 0,
            enferm: 0
        },
        unimed: {
            complex: 0,
            normal: 0,
            apto: 0,
            enferm: 0,
            total: 0
        },
        sus: 0,
        particular: 0
    });

    const [graphSummary, setGraphSummary] = useState([]);
    const [queryType, setQueryType] = useState({value: "week", label: "Semanal"});
    const [totalScheduled, setTotalScheduled] = useState(0);
    const [totalDone, setTotalDone] = useState(0);
    const [percentDone, setPercentDone] = useState(0);

    const [startDate, setStartDate] = useState(dayjs().startOf("month").toDate());
    const [endDate, setEndDate] = useState(dayjs().endOf("month").toDate());

    const donutSeries = proceduresTotals?.length > 0 ? proceduresTotals : [0];
    const donutColors = proceduresColors?.length > 0 ? proceduresColors : ["#C86B2A"];

    useEffect(() => {
        getDoctors();
        getProcedures();
        getPacients();
        getProcedureSummary({ dateStart: startDate, dateEnd: endDate });
        getHospitalsSummary({ dateStart: startDate, dateEnd: endDate });
        getHealthPlansSummary({ dateStart: startDate, dateEnd: endDate });
        getGeneralSummary({ dateStart: startDate, dateEnd: endDate });
        getGraphSummary({ dateStart: startDate, dateEnd: endDate });
    }, [queryType]);

    useEffect(() => {
        getHospitals();
    }, []);

    async function getDoctors() {
        await api.get("/doctors?status=true&lec=true")
            .then(response => {
                if (response.status === 200) {
                    const doctorsReturned = response.data.doctors.map(doctorItem => {
                        return {
                            value: doctorItem._id,
                            label: doctorItem.short_name
                        };
                    });

                    setDoctorsList(doctorsReturned);
                }
            })
    }

    async function getProcedures() {
        await api.get("/procedures?status=true&offset=1&page=1")
            .then(response => {
                if (response.status === 200) {
                    const proceduresReturned = response.data.procedures.map(procedureItem => {
                        return {
                            value: procedureItem._id,
                            label: procedureItem.name
                        };
                    });

                    setProceduresList(proceduresReturned);
                }
            })
    }

    async function getPacients() {
        await api.get("/pacients?status=true&offset=1&page=1")
            .then(response => {
                if (response.status === 200) {
                    setPacientsCount(response.data.total);
                }
            })
    }

    async function getHospitals() {
        await api.get("/hospitals?status=true&offset=1&limit=1000")
            .then(response => {
                if (response.status === 200) {
                    setHospitalsList(response.data.hospitals);
                }
            })
    }

    async function getProcedureSummary({ dateStart = startDate, dateEnd = endDate, doctorId = doctor ? doctor.value : null, procedureId = procedure ? procedure.value : null }) {
        const startDate = dayjs(dateStart).startOf("day").toDate();
        const endDate = dayjs(dateEnd).endOf("day").toDate();

        let filter = "";
        if (doctorId) {
            filter = `&doctorId=${doctorId}`;
        }
        if (procedureId) {
            filter += `&procedureId=${procedureId}`;
        }

        await api.get(`/dashboard/procedures?startDate=${dayjs(startDate).toISOString()}&endDate=${dayjs(endDate).toISOString()}${filter}`)
            .then(response => {
                if (response.status === 200) {
                    const proceduresReturned = [...response.data];
                    const proceduresSorted = proceduresReturned.sort((a, b) => b.count - a.count);
                    const totalProceduresCount = proceduresReturned.reduce((total, item) => total + item.count, 0);

                    setProceduresSummary([...proceduresSorted]);
                    setProceduresCount(totalProceduresCount);

                    let procedureTotal = [];
                    let procedureColor = [];
                    for (let indexProcedure = 0; indexProcedure < Math.min(proceduresSorted.length, 4); indexProcedure++) {
                        procedureTotal.push(proceduresSorted[indexProcedure].count);
                        procedureColor.push(PIECOLORS[indexProcedure]);
                    }
                    setProceduresTotals(procedureTotal);
                    setProceduresColors(procedureColor);
                }
            })
    }

    async function getHospitalsSummary({ dateStart = startDate, dateEnd = endDate, doctorId = doctor ? doctor.value : null, procedureId = procedure ? procedure.value : null }) {
        const startDate = dayjs(dateStart).startOf("day").toDate();
        const endDate = dayjs(dateEnd).endOf("day").toDate();

        let filter = "";
        if (doctorId) {
            filter = `&doctorId=${doctorId}`;
        }
        if (procedureId) {
            filter += `&procedureId=${procedureId}`;
        }

        await api.get(`/dashboard/hospitals?startDate=${dayjs(startDate).toISOString()}&endDate=${dayjs(endDate).toISOString()}${filter}`)
            .then(response => {
                if (response.status === 200) {
                    const hospitalsReturned = [...response.data];
                    setHospitalsSummary(hospitalsReturned);
                }
            })
    }

    async function getHealthPlansSummary({ dateStart = startDate, dateEnd = endDate, doctorId = doctor ? doctor.value : null, procedureId = procedure ? procedure.value : null }) {
        const startDate = dayjs(dateStart).startOf("day").toDate();
        const endDate = dayjs(dateEnd).endOf("day").toDate();

        let filter = "";
        if (doctorId) {
            filter = `&doctorId=${doctorId}`;
        }
        if (procedureId) {
            filter += `&procedureId=${procedureId}`;
        }

        await api.get(`/dashboard/health-plans?startDate=${dayjs(startDate).toISOString()}&endDate=${dayjs(endDate).toISOString()}${filter}`)
            .then(response => {
                if (response.status === 200) {
                    const healthPlansReturned = [...response.data];
                    setHealthPlansSummary(healthPlansReturned);
                }
            })
    }

    async function getGeneralSummary({ dateStart = startDate, dateEnd = endDate, doctorId = doctor ? doctor.value : null, procedureId = procedure ? procedure.value : null }) {
        const startDate = dayjs(dateStart).startOf("day").toDate();
        const endDate = dayjs(dateEnd).endOf("day").toDate();

        let filter = "";
        if (doctorId) {
            filter = `&octorId=${doctorId}`;
        }
        if (procedureId) {
            filter += `&procedureId=${procedureId}`;
        }

        await api.get(`/dashboard/health-plans?startDate=${dayjs(startDate).toISOString()}&endDate=${dayjs(endDate).toISOString()}${filter}`)
            .then(response => {
                if (response.status === 200) {
                    const healthPlansReturned = [...response.data];

                    let generalSummaryReturned = {
                        total: 0,
                        other: {
                            complex: 0,
                            normal: 0,
                            apto: 0,
                            enferm: 0
                        },
                        unimed: {
                            complex: 0,
                            normal: 0,
                            apto: 0,
                            enferm: 0,
                            total: 0
                        },
                        sus: 0,
                        particular: 0
                    };

                    for (let indexHealthPlans = 0; indexHealthPlans < healthPlansReturned.length; indexHealthPlans++) {
                        generalSummaryReturned.total += healthPlansReturned[indexHealthPlans].count;

                        if (healthPlansReturned[indexHealthPlans].health_plan_id === UnimedId) {
                            generalSummaryReturned.unimed.total += healthPlansReturned[indexHealthPlans].count;

                            if (healthPlansReturned[indexHealthPlans].complexity === "COM") {
                                generalSummaryReturned.unimed.complex += healthPlansReturned[indexHealthPlans].count;
                            } else {
                                generalSummaryReturned.unimed.normal += healthPlansReturned[indexHealthPlans].count;
                            }

                            if (healthPlansReturned[indexHealthPlans].accommodation === "APT") {
                                generalSummaryReturned.unimed.apto += healthPlansReturned[indexHealthPlans].count;
                            } else {
                                generalSummaryReturned.unimed.enferm += healthPlansReturned[indexHealthPlans].count;
                            }
                        } else if (healthPlansReturned[indexHealthPlans].health_plan_id === SusId) {
                            generalSummaryReturned.sus += healthPlansReturned[indexHealthPlans].count;
                        } else if (healthPlansReturned[indexHealthPlans].health_plan_id === ParticularId) {
                            generalSummaryReturned.particular += healthPlansReturned[indexHealthPlans].count;
                        } else {
                            if (healthPlansReturned[indexHealthPlans].complexity === "COM") {
                                generalSummaryReturned.other.complex += healthPlansReturned[indexHealthPlans].count;
                            } else {
                                generalSummaryReturned.other.normal += healthPlansReturned[indexHealthPlans].count;
                            }

                            if (healthPlansReturned[indexHealthPlans].accommodation === "APT") {
                                generalSummaryReturned.other.apto += healthPlansReturned[indexHealthPlans].count;
                            } else {
                                generalSummaryReturned.other.enferm += healthPlansReturned[indexHealthPlans].count;
                            }
                        }
                    }

                    setGeneralSummary(generalSummaryReturned);
                }
            })
    }

    async function getGraphSummary({ dateStart = startDate, dateEnd = endDate, doctorId = doctor ? doctor.value : null, procedureId = procedure ? procedure.value : null }) {
        const startDate = dayjs(dateStart).startOf("day").toDate();
        const endDate = dayjs(dateEnd).endOf("day").toDate();

        let filter = "";
        if (doctorId) {
            filter = `&doctorId=${doctorId}`;
        }
        if (procedureId) {
            filter += `&procedureId=${procedureId}`;
        }

        await api.get(`/dashboard/graph-procedures?queryType=${queryType.value}&startDate=${dayjs(startDate).toISOString()}&endDate=${dayjs(endDate).toISOString()}${filter}`)

            .then(response => {
                if (response.status === 200) {
                    const graphReturned = [...response.data];
                    setGraphSummary(graphReturned);

                    let subTotalScheduled = 0;
                    let subTotalDone = 0;
                    graphReturned.forEach(graphItem => {
                        const graphCount = [...graphItem.count];
                        graphCount.forEach(countItem => {
                            subTotalScheduled += countItem.count;

                            if (countItem.status === "done") {
                                subTotalDone += countItem.count;
                            }
                        })
                    })
                    setTotalScheduled(subTotalScheduled);
                    setTotalDone(subTotalDone);

                    if (subTotalScheduled > 0) {
                        const subPercentDone = ((subTotalDone * 100) / subTotalScheduled).toFixed(0);
                        setPercentDone(subPercentDone);
                    }
                }
            })
    }

    function handleChangeDoctor(e) {
        setDoctor(e);
        console.log(e)

        if (e !== null) {
            getProcedureSummary({ doctorId: e.value });
            getHospitalsSummary({ doctorId: e.value });
            getHealthPlansSummary({ doctorId: e.value });
            getGeneralSummary({ doctorId: e.value });
            getGraphSummary({ doctorId: e.value });
        } else {
            getProcedureSummary({ dateStart: startDate, dateEnd: endDate, doctorId: e });
            getHospitalsSummary({ dateStart: startDate, dateEnd: endDate, doctorId: e });
            getHealthPlansSummary({ dateStart: startDate, dateEnd: endDate, doctorId: e });
            getGeneralSummary({ dateStart: startDate, dateEnd: endDate, doctorId: e });
            getGraphSummary({ dateStart: startDate, dateEnd: endDate, doctorId: e });
        }
    }

    function handleChangeProcedure(e) {
        setProcedure(e);
        console.log(e)

        if (e !== null) {
            getProcedureSummary({ procedureId: e.value });
            getHospitalsSummary({ procedureId: e.value });
            getHealthPlansSummary({ procedureId: e.value });
            getGeneralSummary({ procedureId: e.value });
            getGraphSummary({ procedureId: e.value });
        } else {
            getProcedureSummary({ dateStart: startDate, dateEnd: endDate, procedureId: e });
            getHospitalsSummary({ dateStart: startDate, dateEnd: endDate, procedureId: e });
            getHealthPlansSummary({ dateStart: startDate, dateEnd: endDate, procedureId: e });
            getGeneralSummary({ dateStart: startDate, dateEnd: endDate, procedureId: e });
            getGraphSummary({ dateStart: startDate, dateEnd: endDate, procedureId: e });
        }
    }

    function handleChangePeriod(newStartDate, newEndDate) {
        if (!newStartDate || !newEndDate) {
            return;
        }

        getProcedureSummary({ dateStart: newStartDate, dateEnd: newEndDate });
        getHospitalsSummary({ dateStart: newStartDate, dateEnd: newEndDate });
        getHealthPlansSummary({ dateStart: newStartDate, dateEnd: newEndDate });
        getGeneralSummary({ dateStart: newStartDate, dateEnd: newEndDate });
        getGraphSummary({ dateStart: newStartDate, dateEnd: newEndDate });

        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }

    function handleExportAttendance() {
        const unitLabel = queryType.value === "week" ? "Dia" : "Mês";
        const rows = buildAttendanceData(graphSummary, queryType.value).map(item => ({
            [unitLabel]: item.label,
            Agendados: item.Agendados,
            Realizados: item.Realizados
        }));
        exportToExcel(rows, "Atendimentos", "atendimentos");
    }

    function handleExportProcedures() {
        const rows = proceduresSummary.slice(0, 4).map(item => ({
            Procedimento: item.procedure_name,
            Quantidade: item.count,
            "%": proceduresCount > 0 ? Number(((100 * item.count) / proceduresCount).toFixed(2)) : 0
        }));
        exportToExcel(rows, "Procedimentos", "procedimentos");
    }

    function handleExportDashboardExcel() {
        const workbook = XLSX.utils.book_new();

        const filtersSheetRows = [
            { Filtro: "Periodo", Valor: queryType?.label || "Semanal" },
            { Filtro: "Medico", Valor: doctor?.label || "Todos" },
            { Filtro: "Procedimento", Valor: procedure?.label || "Todos" },
            { Filtro: "Data de Inicio", Valor: dayjs(startDate).format("DD/MM/YYYY") },
            { Filtro: "Data Final", Valor: dayjs(endDate).format("DD/MM/YYYY") }
        ];

        const proceduresSheetRows = (proceduresSummary || []).map((item) => ({
            Procedimento: item?.procedure_name || "",
            Quantidade: Number(item?.count || 0)
        }));

        const hospitalsSheetRows = (hospitalsSummary || []).map((item) => ({
            Hospital: item?.hospital_name || "",
            Mes: Number(item?.month || 0),
            Complexidade: item?.complexity || "",
            Quantidade: Number(item?.count || 0)
        }));

        const healthPlansSheetRows = (healthPlansSummary || []).map((item) => ({
            Convenio: item?.health_plan_name || "",
            Mes: Number(item?.month || 0),
            Complexidade: item?.complexity || "",
            Acomodacao: item?.accommodation || "",
            Quantidade: Number(item?.count || 0)
        }));

        const graphSheetRows = [];
        (graphSummary || []).forEach((timeItem) => {
            (timeItem?.count || []).forEach((countItem) => {
                graphSheetRows.push({
                    UnidadeTempo: Number(timeItem?.timeUnit || 0),
                    Status: countItem?.status || "",
                    Quantidade: Number(countItem?.count || 0)
                });
            });
        });

        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(filtersSheetRows), "Filtros");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(proceduresSheetRows), "Procedimentos");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(hospitalsSheetRows), "Hospitais");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(healthPlansSheetRows), "Convenios");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(graphSheetRows), "Atendimentos");

        const timestamp = dayjs().format("YYYYMMDD_HHmmss");
        XLSX.writeFile(workbook, `dashboard_relatorio_${timestamp}.xlsx`);
    }

    return (
        <>

            <div className="row">
                <div className="col-xl-12">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="page-titles style1 d-flex justify-content-between align-items-center">
                                <div className="align-items-center">
                                    <h2 className="heading">Dashboard</h2>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div id="doctorpicker" className="input-group date dz-select">
                                        <span className="input-group-addon d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="16" width="18" viewBox="0 0 576 512">
                                                <path d="M64 64c0-17.7-14.3-32-32-32S0 46.3 0 64V400c0 44.2 35.8 80 80 80H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H80c-8.8 0-16-7.2-16-16V64zm406.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L320 210.7l-57.4-57.4c-12.5-12.5-32.8-12.5-45.3 0l-112 112c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L240 221.3l57.4 57.4c12.5 12.5 32.8 12.5 45.3 0l128-128z" />
                                            </svg>
                                        </span>
                                        <div className="calender-picker">
                                            <Select
                                                defaultValue={queryType}
                                                value={queryType}
                                                onChange={e => setQueryType(e)}
                                                options={[
                                                    {value: "week", label: "Semanal"},
                                                    {value: "year", label: "Anual"}
                                                ]}
                                                styles={{
                                                    control: (baseStyles, state) => ({
                                                        ...baseStyles,
                                                        height: 45,
                                                        backgroundColor: state.isDisabled ? "#dde0e3" : ""
                                                    }),
                                                }}
                                                placeholder="Selecione para filtrar..."
                                            />
                                        </div>
                                    </div>

                                    <div id="doctorpicker" className="input-group date dz-select">
                                        <span className="input-group-addon d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="16" width="18" viewBox="0 0 576 512">
                                                <path d="M142.4 21.9c5.6 16.8-3.5 34.9-20.2 40.5L96 71.1V192c0 53 43 96 96 96s96-43 96-96V71.1l-26.1-8.7c-16.8-5.6-25.8-23.7-20.2-40.5s23.7-25.8 40.5-20.2l26.1 8.7C334.4 19.1 352 43.5 352 71.1V192c0 77.2-54.6 141.6-127.3 156.7C231 404.6 278.4 448 336 448c61.9 0 112-50.1 112-112V265.3c-28.3-12.3-48-40.5-48-73.3c0-44.2 35.8-80 80-80s80 35.8 80 80c0 32.8-19.7 61-48 73.3V336c0 97.2-78.8 176-176 176c-92.9 0-168.9-71.9-175.5-163.1C87.2 334.2 32 269.6 32 192V71.1c0-27.5 17.6-52 43.8-60.7l26.1-8.7c16.8-5.6 34.9 3.5 40.5 20.2zM480 224a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
                                            </svg>
                                        </span>
                                        <div className="calender-picker">
                                            <Select
                                                defaultValue={doctor}
                                                value={doctor}
                                                onChange={e => handleChangeDoctor(e)}
                                                options={doctorsList}
                                                isClearable
                                                styles={{
                                                    control: (baseStyles, state) => ({
                                                        ...baseStyles,
                                                        height: 45,
                                                        backgroundColor: state.isDisabled ? "#dde0e3" : ""
                                                    }),
                                                }}
                                                placeholder="Selecione para filtrar..."
                                            />
                                        </div>
                                    </div>

                                    <div id="procedurepicker" className="input-group date dz-select">
                                        <span className="input-group-addon d-flex align-items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" height="16" width="18" viewBox="0 0 384 512">
                                                <path d="M320 64H288.6c-11.7-19.6-32.9-32-56.6-32H152c-23.6 0-44.8 12.4-56.6 32H64C28.7 64 0 92.7 0 128V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64zM192 64c8.8 0 16 7.2 16 16s-7.2 16-16 16s-16-7.2-16-16s7.2-16 16-16zM96 216c0-13.3 10.7-24 24-24h144c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24zm0 80c0-13.3 10.7-24 24-24h144c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24zm0 80c0-13.3 10.7-24 24-24h72c13.3 0 24 10.7 24 24s-10.7 24-24 24H120c-13.3 0-24-10.7-24-24z"/>
                                            </svg>
                                        </span>
                                        <div className="calender-picker">
                                            <Select
                                                defaultValue={procedure}
                                                value={procedure}
                                                onChange={e => handleChangeProcedure(e)}
                                                options={proceduresList}
                                                isClearable
                                                styles={{
                                                    control: (baseStyles, state) => ({
                                                        ...baseStyles,
                                                        height: 45,
                                                        backgroundColor: state.isDisabled ? "#dde0e3" : ""
                                                    }),
                                                }}
                                                placeholder="Selecione para filtrar..."
                                            />
                                        </div>
                                    </div>

                                    <div className="input-group date dz-select" style={{ marginRight: 8 }}>
                                        <div className="calender-picker" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                            <h6 className="fs-14 mb-1 ms-1 font-w600">Exportar Relatorio:</h6>
                                            <button
                                                type="button"
                                                className="btn btn-primary d-flex align-items-center justify-content-center"
                                                style={{ height: 45, width: 45, padding: 0 }}
                                                onClick={handleExportDashboardExcel}
                                                aria-label="Exportar relatorio em Excel"
                                                title="Exportar relatorio"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                                                    <path d="M256 0a32 32 0 0 1 32 32V320h56.6c14.3 0 21.5 17.3 11.3 27.3l-96 96c-6.2 6.2-16.4 6.2-22.6 0l-96-96C131.1 337.3 138.3 320 152.6 320H208V32a32 32 0 0 1 32-32h16zm-224 416a32 32 0 0 1 32-32h384a32 32 0 1 1 0 64H64a32 32 0 0 1-32-32z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    <div id="datepicker" className="input-group date dz-calender" data-date-format="mm-dd-yyyy">
                                        <span className="input-group-addon d-flex align-items-center">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 14.0001C12.1978 14.0001 12.3911 13.9415 12.5556 13.8316C12.72 13.7217 12.8482 13.5655 12.9239 13.3828C12.9996 13.2001 13.0194 12.999 12.9808 12.805C12.9422 12.611 12.847 12.4329 12.7071 12.293C12.5673 12.1531 12.3891 12.0579 12.1951 12.0193C12.0011 11.9807 11.8 12.0005 11.6173 12.0762C11.4346 12.1519 11.2784 12.2801 11.1685 12.4445C11.0586 12.609 11 12.8023 11 13.0001C11 13.2653 11.1054 13.5197 11.2929 13.7072C11.4804 13.8947 11.7348 14.0001 12 14.0001ZM17 14.0001C17.1978 14.0001 17.3911 13.9415 17.5556 13.8316C17.72 13.7217 17.8482 13.5655 17.9239 13.3828C17.9996 13.2001 18.0194 12.999 17.9808 12.805C17.9422 12.611 17.847 12.4329 17.7071 12.293C17.5673 12.1531 17.3891 12.0579 17.1951 12.0193C17.0011 11.9807 16.8 12.0005 16.6173 12.0762C16.4346 12.1519 16.2784 12.2801 16.1685 12.4445C16.0586 12.609 16 12.8023 16 13.0001C16 13.2653 16.1054 13.5197 16.2929 13.7072C16.4804 13.8947 16.7348 14.0001 17 14.0001ZM12 18.0001C12.1978 18.0001 12.3911 17.9415 12.5556 17.8316C12.72 17.7217 12.8482 17.5655 12.9239 17.3828C12.9996 17.2001 13.0194 16.999 12.9808 16.805C12.9422 16.611 12.847 16.4328 12.7071 16.293C12.5673 16.1531 12.3891 16.0579 12.1951 16.0193C12.0011 15.9807 11.8 16.0005 11.6173 16.0762C11.4346 16.1519 11.2784 16.2801 11.1685 16.4445C11.0586 16.609 11 16.8023 11 17.0001C11 17.2653 11.1054 17.5197 11.2929 17.7072C11.4804 17.8947 11.7348 18.0001 12 18.0001ZM17 18.0001C17.1978 18.0001 17.3911 17.9415 17.5556 17.8316C17.72 17.7217 17.8482 17.5655 17.9239 17.3828C17.9996 17.2001 18.0194 16.999 17.9808 16.805C17.9422 16.611 17.847 16.4328 17.7071 16.293C17.5673 16.1531 17.3891 16.0579 17.1951 16.0193C17.0011 15.9807 16.8 16.0005 16.6173 16.0762C16.4346 16.1519 16.2784 16.2801 16.1685 16.4445C16.0586 16.609 16 16.8023 16 17.0001C16 17.2653 16.1054 17.5197 16.2929 17.7072C16.4804 17.8947 16.7348 18.0001 17 18.0001ZM7 14.0001C7.19778 14.0001 7.39112 13.9415 7.55557 13.8316C7.72002 13.7217 7.84819 13.5655 7.92388 13.3828C7.99957 13.2001 8.01937 12.999 7.98078 12.805C7.9422 12.611 7.84696 12.4329 7.70711 12.293C7.56725 12.1531 7.38907 12.0579 7.19509 12.0193C7.00111 11.9807 6.80004 12.0005 6.61732 12.0762C6.43459 12.1519 6.27841 12.2801 6.16853 12.4445C6.05865 12.609 6 12.8023 6 13.0001C6 13.2653 6.10536 13.5197 6.29289 13.7072C6.48043 13.8947 6.73478 14.0001 7 14.0001ZM19 4.00011H18V3.00011C18 2.73489 17.8946 2.48054 17.7071 2.293C17.5196 2.10546 17.2652 2.00011 17 2.00011C16.7348 2.00011 16.4804 2.10546 16.2929 2.293C16.1054 2.48054 16 2.73489 16 3.00011V4.00011H8V3.00011C8 2.73489 7.89464 2.48054 7.70711 2.293C7.51957 2.10546 7.26522 2.00011 7 2.00011C6.73478 2.00011 6.48043 2.10546 6.29289 2.293C6.10536 2.48054 6 2.73489 6 3.00011V4.00011H5C4.20435 4.00011 3.44129 4.31618 2.87868 4.87879C2.31607 5.4414 2 6.20446 2 7.00011V19.0001C2 19.7958 2.31607 20.5588 2.87868 21.1214C3.44129 21.684 4.20435 22.0001 5 22.0001H19C19.7956 22.0001 20.5587 21.684 21.1213 21.1214C21.6839 20.5588 22 19.7958 22 19.0001V7.00011C22 6.20446 21.6839 5.4414 21.1213 4.87879C20.5587 4.31618 19.7956 4.00011 19 4.00011ZM20 19.0001C20 19.2653 19.8946 19.5197 19.7071 19.7072C19.5196 19.8947 19.2652 20.0001 19 20.0001H5C4.73478 20.0001 4.48043 19.8947 4.29289 19.7072C4.10536 19.5197 4 19.2653 4 19.0001V10.0001H20V19.0001ZM20 8.00011H4V7.00011C4 6.73489 4.10536 6.48054 4.29289 6.293C4.48043 6.10546 4.73478 6.00011 5 6.00011H19C19.2652 6.00011 19.5196 6.10546 19.7071 6.293C19.8946 6.48054 20 6.73489 20 7.00011V8.00011ZM7 18.0001C7.19778 18.0001 7.39112 17.9415 7.55557 17.8316C7.72002 17.7217 7.84819 17.5655 7.92388 17.3828C7.99957 17.2001 8.01937 16.999 7.98078 16.805C7.9422 16.611 7.84696 16.4328 7.70711 16.293C7.56725 16.1531 7.38907 16.0579 7.19509 16.0193C7.00111 15.9807 6.80004 16.0005 6.61732 16.0762C6.43459 16.1519 6.27841 16.2801 6.16853 16.4445C6.05865 16.609 6 16.8023 6 17.0001C6 17.2653 6.10536 17.5197 6.29289 17.7072C6.48043 17.8947 6.73478 18.0001 7 18.0001Z" fill="var(--primary)" />
                                            </svg>
                                        </span>
                                        <div className="calender-picker">
                                            <h6 className="fs-14 mb-0 ms-2 font-w600">Período</h6>
                                            <div className="d-flex">
                                                <div>
                                                    <small className="ms-2">Data de Início</small>
                                                    <DatePicker selected={startDate} className="form-control"
                                                        onChange={(date) => handleChangePeriod(date, endDate)}
                                                        selectsStart
                                                        startDate={startDate}
                                                        endDate={endDate}
                                                        maxDate={endDate}
                                                        locale="ptBR"
                                                        dateFormat="P"
                                                        customInput={
                                                            <MaskedInput mask="11/11/1111" placeholder="dd/mm/yyyy" />
                                                        }
                                                    />
                                                </div>
                                                <div className="ms-2">
                                                    <small className="ms-2">Data Final</small>
                                                    <DatePicker selected={endDate} className="form-control"
                                                        onChange={(date) => handleChangePeriod(startDate, date)}
                                                        selectsEnd
                                                        startDate={startDate}
                                                        endDate={endDate}
                                                        minDate={startDate}
                                                        locale="ptBR"
                                                        dateFormat="P"
                                                        customInput={
                                                            <MaskedInput mask="11/11/1111" placeholder="dd/mm/yyyy" />
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <SummarySlider
                        pacientCount={pacientsCount}
                        procedureCount={proceduresCount}
                    /> */}

                    <div className="row">
                        <div className="col-xl-8" >
                            <div className="card crypto-chart ">
                                <div className="card-header pb-0 border-0 flex-wrap">
                                    <div className="mb-2 mb-sm-0">
                                        <div className="chart-title mb-3">
                                            <h2 className="heading">{`Atendimentos - ${dayjs(startDate).format("DD/MM/YYYY")} a ${dayjs(endDate).format("DD/MM/YYYY")}`}</h2>
                                        </div>
                                        <div className="d-flex align-items-center mb-3 mb-sm-0">
                                            <div className="round weekly" id="dzOldSeries">
                                                <div>
                                                    <svg className="me-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect width="14" height="14" rx="4" fill={"#C86B2A"} />
                                                    </svg>
                                                    <span className="fs-14">Agendados</span>
                                                    <h4 className="fs-5 font-w600 mb-0">{totalScheduled.toLocaleString("pt-br")}</h4>
                                                </div>
                                            </div>
                                            <div className="round " id="dzNewSeries">
                                                <div>
                                                    <svg className="me-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <rect width="14" height="14" rx="4" fill={"#374B45"} />
                                                    </svg>
                                                    <span className="fs-14">Realizados</span>
                                                    <h4 className="fs-5 font-w600 mb-0">{totalDone.toLocaleString("pt-br")}</h4>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-static">
                                        <div className="d-flex justify-content-end mb-2">
                                            <ExportButton onClick={handleExportAttendance} />
                                        </div>
                                        {/* <div className="d-flex align-items-center mb-3 ">
                                            <Dropdown className="me-4 drop-select">
                                                <Dropdown.Toggle as="div" className="i-false drop-select-btn">{selectBtn2} <i className="fa-solid fa-angle-down"></i></Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item key={1} onClick={() => setSelectBtn2("Este Mẽs")}>Este Mês</Dropdown.Item>
                                                    <Dropdown.Item key={2} onClick={() => setSelectBtn2("Esta Semana")}>Esta Semana</Dropdown.Item>
                                                    <Dropdown.Item key={3} onClick={() => setSelectBtn2("Hoje")}>Hoje</Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div> */}
                                        <div className="progress-content" style={{ width: "15rem" }}>
                                            <div className="d-flex justify-content-between">
                                                <h6>Total Realizado</h6>
                                                <span className="pull-end">{percentDone}%</span>
                                            </div>
                                            <div className="progress mt-1">
                                                <div className="progress-bar bg-primary" style={{ width: `${percentDone}%`, height: "100%" }} role="progressbar">
                                                    {/* <span className="sr-only">{percentDone}% Complete</span> */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body pt-2 custome-tooltip pb-0">
                                    <ProceduresSummary proceduresSummary={graphSummary} queryType={queryType.value}/>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 wow fadeInUp" data-wow-delay="1s">
                            <div className="card">
                                <div className="card-header border-0">
                                    <h2 className="heading">Procedimentos - Mês (top 4)</h2>
                                    <ExportButton onClick={handleExportProcedures} />
                                </div>
                                <div className="card-body text-center pt-0 pb-2">
                                    {/* <EmailPieChart /> */}
                                    <div id="pieChart" className="d-inline-block">
                                        <ReactApexChart
                                            options={{
                                                chart: {
                                                    type: 'donut',
                                                    height: 200,
                                                    innerRadius: 50,
                                                },
                                                dataLabels: {
                                                    enabled: false,
                                                },
                                                stroke: {
                                                    width: 0,
                                                },
                                                plotOptions: {
                                                    pie: {
                                                        startAngle: 0,
                                                        endAngle: 360,
                                                        donut: {
                                                            size: '80%',
                                                        },
                                                    },
                                                },
                                                colors: donutColors,
                                                legend: {
                                                    position: 'bottom',
                                                    show: false
                                                },
                                                responsive: [{
                                                    breakpoint: 768,
                                                    options: {
                                                        chart: {
                                                            width: 200
                                                        },
                                                    }
                                                }]

                                            }}
                                            series={donutSeries}
                                            type="donut"
                                            height={200}
                                        />
                                    </div>
                                    <div className="chart-items">
                                        <div className="row">
                                            <div className=" col-xl-12 col-sm-12">
                                                <div className="text-start mt-2">
                                                    {proceduresSummary.slice(0, 4).map((procedureItem, indexProcedure) => {
                                                        return (
                                                            <div className="color-picker" key={procedureItem.procedure_id}>
                                                                <span className="mb-0 col-6 fs-14">
                                                                    <svg className="me-2" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <rect width="14" height="14" rx="4" fill={proceduresColors[indexProcedure]} />
                                                                    </svg>
                                                                    {procedureItem.procedure_name} ({((100 * procedureItem.count) / proceduresCount).toFixed(0)}%)
                                                                </span>
                                                                <h5>{procedureItem.count}</h5>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 wow fadeInUp" data-wow-delay="1.5s">

                            <div className="card">
                                <HospitalProcedure hospitalSummary={hospitalsSummary} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 wow fadeInUp" data-wow-delay="1.5s">

                            <div className="card">
                                <HospitalGoal hospitalSummary={hospitalsSummary} hospitals={hospitalsList} startDate={startDate} endDate={endDate} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 wow fadeInUp" data-wow-delay="1.5s">

                            <div className="card">
                                <HealthPlanProcedure healthPlanSummary={healthPlansSummary} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 wow fadeInUp" data-wow-delay="1.5s">

                            <div className="card">
                                <GeneralSummary generalSummary={generalSummary} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default Dashboard;